import { createInsight } from "features/insights/reducer/InsightActions";
import { OverlayPanel } from "primereact/overlaypanel";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FlexAlignCenter, GenericButton } from "shared/ui-basics";
import InsightType from "shared/ui-basics/ui-components/InsightType";
import styled from "styled-components/macro";
import { createDefaultInsight, EMPTY_GUID, encode } from "utils/helpers";

interface InsightCreateNewItemProps {
	link?: (id: string, e: any) => void;
	input: string;
	clearInput: () => void;
}

const InsightCreateNewItem: React.FC<InsightCreateNewItemProps> = ({ link, input, clearInput }) => {
	const { t } = useTranslation();
	const op = useRef();
	const insightTypes = useSelector((state: any) => state.libraries?.insightTypes);
	const userInfo = useSelector(({ auth }: any) => auth?.userInfo);

	const getTypeId = () => {
		const typeId = localStorage.getItem("lastUsedInsightTypeId");
		if (insightTypes?.find((it: any) => it.id === typeId)) return typeId;
		else if (insightTypes?.find((it: any) => it.id === insightTypes[0]?.id)) return insightTypes[0]?.id;
		return EMPTY_GUID;
	};
	const [selectedTypeId, setSelectedTypeId] = useState(getTypeId());

	const handleCreate = (e: any) => {
		clearInput();
		localStorage.setItem("lastUsedInsightTypeId", selectedTypeId);
		const currentTypeId = insightTypes?.find((it: any) => it.id === selectedTypeId) ? selectedTypeId : EMPTY_GUID;

		const item = createDefaultInsight({ currentUser: userInfo, name: encode(input.truncate(100, true)), typeId: currentTypeId });
		createInsight({ item, onSuccess: link ? () => link(item.id, e) : () => {}});
    };
    
    const handleSelect = (e, typeId) => {
        e?.stopPropagation()
        setSelectedTypeId(typeId)
        op?.current?.toggle(e)
    }

	return (
		<MenuItemContainer>
			<NewItemCreation>
				<FlexAlignCenter gap="8">
					<GenericButton variant="dark-outline" size="xs" onClick={handleCreate}>
						{t("JS_87")}
					</GenericButton>
					<Text>{input}</Text>
				</FlexAlignCenter>
				<GenericButton variant="light" icon="chevron-down" iconPosition="after" iconClassName="ms-2" onClick={(e: any) => op.current.toggle(e)}>
                    <div>{selectedTypeId !== EMPTY_GUID ? <InsightType typeId={selectedTypeId} /> : t("PLEASE SELECT_TYPE")}</div>
				</GenericButton>
				<CustomOverlayPanel ref={op}>
					<ItemsContainer>
						{insightTypes
							?.filter((it) => it?.id !== selectedTypeId)
							?.map((it) => (
								<TypeContainer key={it?.id} onClick={(e) => handleSelect(e, it?.id)}>
									<InsightType typeId={it?.id} />
								</TypeContainer>
							))}
					</ItemsContainer>
				</CustomOverlayPanel>
			</NewItemCreation>
		</MenuItemContainer>
	);
};

InsightCreateNewItem.displayName = "InsightCreateNewItem";
export default InsightCreateNewItem;

const MenuItemContainer = styled.div`
	width: 100%;
`;

const NewItemCreation = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 4px;
	width: 100%;
	gap: 8px;

	:hover {
		border-radius: 4px;
		background: #f5f5f5;
	}
`;

const Text = styled.div`
	overflow: hidden;
	color: #262626;
	text-overflow: ellipsis;
	font-family: "Inter";
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
`;

const CustomOverlayPanel = styled(OverlayPanel)`
	.p-overlaypanel-content {
		padding: 0px;
	}

	.p-overlaypanel-close {
		background: #f96209;
		color: #ffffff;
		width: 1.5rem;
		height: 1.5rem;
		transition: background-color 0.2s, color 0.2s, box-shadow 0.2s;
		border-radius: 50%;
		position: absolute;
		top: -12px;
		right: -12px;
	}
`;

const ItemsContainer = styled.div`
	height: 100%;
	width: 100%;
	padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 200px;
    overflow: scroll;
`;

const TypeContainer = styled.div`
	border-radius: 4px;
	padding: 4px;
	:hover {
        cursor: pointer;
		background-color: #efefef;
	}
`;
